// ─── Route access levels ────────────────────────────────────────────────────
//
// THE PRINCIPLE: consent attaches at the first byte stored or transmitted
// about a visitor — never at the door. Reading stores nothing, so reading is
// never gated. Asking for consent before anything happens is consent theatre,
// and it trains people to click through without reading. We ask less often so
// that each ask means more.
//
// The canonical statement is BECIVITAS.md, "Privacy: consent attaches at the
// first byte". If this comment and that document disagree, that one wins.
//
// TIER 0 — reading. No gate, no consent, nothing stored. <Layout>
//   / /how-it-works /about /privacy /terms /candidates /candidates/:id
//   Verified 2026-10-09: About and HowItWorks make zero storage and zero
//   backend calls; Candidates READS civitas_state to filter and writes nothing.
//
// TIER 1 — stores something about the visitor. Guest consent, local only.
//   <Protected allowGuest>
//   /onboarding      writes civitas_state, civitas_district, civitas_district_keys
//   /questionnaire   answers live in React state; consent attaches on SAVE
//   /results         requires persisted answers (loadUserAnswers,
//                    hasCompletedQuestionnaire), so consent attaches no later
//                    than "See My Matches"
//
// TIER 2 — leaves the device. Needs its own explicit, per-action consent,
//   not one inherited from a blanket agreement clicked three screens earlier.
//   Address lookup transmits the voter's address to the Census proxy.
//   Account creation.
//
// AUTH REQUIRED, always <Protected>:  /compare /feedback /admin /dashboard
//
// ────────────────────────────────────────────────────────────────────────────
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import Header from './components/Header'
import Footer from './components/Footer'
import BetaReleaseNotice from './components/BetaReleaseNotice'
import Home from './pages/Home'
import Questionnaire from './pages/Questionnaire'
import Results from './pages/Results'
import Candidates from './pages/Candidates'
import CandidateDetail from './pages/CandidateDetail'
import Compare from './pages/Compare'
import HowItWorks from './pages/HowItWorks'
import About from './pages/About'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import Feedback from './pages/Feedback'
import NotFound from './pages/NotFound'
import Admin from './pages/Admin'
import Auth from './pages/Auth'
import AuthCallback from './pages/AuthCallback'
import BetaAgreement from './pages/BetaAgreement'
import VerifyEmail from './pages/VerifyEmail'
import DistrictOnboarding from './pages/DistrictOnboarding'
import Dashboard from './pages/Dashboard'

const Layout = ({ children }) => (
  <div className="flex min-h-screen flex-col">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
)

// Voter routes support guests with browser-local consent and preferences.
// Account features and administrative routes retain authentication checks.
const Protected = ({ children, adminOnly, allowGuest = false }) => (
  <ProtectedRoute adminOnly={adminOnly} allowGuest={allowGuest}>
    <Layout>{children}</Layout>
  </ProtectedRoute>
)

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BetaReleaseNotice />
        <Routes>
          <Route path="/login"           element={<Auth />} />
          <Route path="/callback"        element={<AuthCallback />} />
          <Route path="/auth/callback"   element={<AuthCallback />} />
          <Route path="/verify-email"    element={<VerifyEmail />} />
          <Route path="/beta-agreement"  element={<BetaAgreement />} />
          <Route path="/onboarding"      element={<ProtectedRoute allowGuest><DistrictOnboarding /></ProtectedRoute>} />
          <Route path="/"                element={<Layout><Home /></Layout>} />
          <Route path="/questionnaire"   element={<Protected allowGuest><Questionnaire /></Protected>} />
          <Route path="/results"         element={<Protected allowGuest><Results /></Protected>} />
          <Route path="/candidates"      element={<Layout><Candidates /></Layout>} />
          <Route path="/candidates/:id"  element={<Layout><CandidateDetail /></Layout>} />
          <Route path="/compare"         element={<Protected allowGuest><Compare /></Protected>} />
          <Route path="/how-it-works"    element={<Layout><HowItWorks /></Layout>} />
          <Route path="/about"           element={<Layout><About /></Layout>} />
          <Route path="/privacy"         element={<Layout><Privacy /></Layout>} />
          <Route path="/terms"           element={<Layout><Terms /></Layout>} />
          <Route path="/feedback"        element={<Layout><Feedback /></Layout>} />
          <Route path="/admin"           element={<Protected adminOnly={true}><Admin /></Protected>} />
          <Route path="/dashboard"       element={<Protected adminOnly={true}><Dashboard /></Protected>} />
          <Route path="*"                element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
